import { useEffect, useRef } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useChatStore } from '@/stores/chat-store';
import { AiMessageBubble } from './ai-message-bubble';
import { AiStreamingMessage } from './ai-streaming-message';
import { AiChatInput } from './ai-chat-input';
import vtakLogo from '@/assets/vtak-logo.png';

/**
 * Active AI conversation thread (headerless): auto-scrolling message history,
 * streaming response, and input dock. Shared by the sidebar panel and the
 * floating chat window — each host supplies its own header.
 */
export function AiConversationThread(): React.JSX.Element {
  const aiMessages = useChatStore((s) => s.aiMessages);
  const isAiStreaming = useChatStore((s) => s.isAiStreaming);
  const aiStreamContent = useChatStore((s) => s.aiStreamContent);
  const activeConversationId = useChatStore((s) => s.activeConversationId);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aiMessages, aiStreamContent]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden p-3">
      <ScrollArea className="flex-1 space-y-1.5">
        {aiMessages.length === 0 && !isAiStreaming && (
          <div className="flex flex-col items-center gap-3 py-12">
            <img src={vtakLogo} alt="VTAK AI" className="h-6 w-auto opacity-60" />
            <p className="max-w-[220px] text-center text-xs text-muted-foreground">
              Ask about aircraft, satellites, and anything else on the map.
            </p>
          </div>
        )}
        {aiMessages.map((msg) => (
          <AiMessageBubble key={msg.id} message={msg} />
        ))}
        {isAiStreaming && aiStreamContent && <AiStreamingMessage content={aiStreamContent} />}
        <div ref={messagesEndRef} />
      </ScrollArea>

      <AiChatInput conversationId={activeConversationId} />
    </div>
  );
}
